import React, { useState, useMemo, useContext, useRef, useEffect } from "react";
import { LanguageContext, StaffContext } from "../../../../../Context";
import Lang from "../../../components/Lang";
import Loading from "../../../components/Loading";
import { formatDistanceToNow } from "date-fns";
import { MentionsInput, Mention } from 'react-mentions';
import "../Home.css";
import useCTX from "../../../hooks/useCTX";
import mut from "../../../hooks/mut";
import useDBO from "../../../hooks/useDBO";
import HtClass from "../../../components/HtClass";
import StudentLink from "../../../components/StudentLink";
import { Avatar } from "@mui/material";
import { FileText, Globe, GraduationCap, Briefcase, AlertTriangle, Inbox, School } from "lucide-react";


// Custom styles for react-mentions component
const defaultStyle = {
  control: {
    backgroundColor: '#fff',
    fontSize: 14,
    fontWeight: '500',
    borderRadius: '12px',
  },
  highlighter: {
    padding: '0.75rem',
    border: '1px solid transparent',
  },
  input: {
    padding: '0.75rem',
    borderRadius: '12px',
    border: '1px solid #cbd5e1',
    outline: 'none',
  },
  suggestions: {
    list: {
      backgroundColor: 'white',
      border: '1px solid #e2e8f0',
      fontSize: 14,
      borderRadius: 8,
      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
      position: 'absolute',
      bottom: '14px', // Shift it up above the input
      top: 'auto',
    },
    item: {
      padding: '8px 12px',
      borderBottom: '1px solid #f1f5f9',
      '&focused': {
        backgroundColor: '#f0fdfa',
      },
    },
  },
};

export default function StudentNotes() {
  const { user, students: allStudentsContext, findStaffName, findStaff } = useCTX();
  const { l, language } = useContext(LanguageContext);
  const hasElevatedRole = user.roles.includes("Manager") || user.roles.includes("Admin") || user.roles.includes("Owner");
  const [isManagerMode, setIsManagerMode] = useState(hasElevatedRole);
  
  const [noteText, setNoteText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localNotes, setLocalNotes] = useState([]);
  const [visibleCount, setVisibleCount] = useState(10);
  const loadMoreRef = useRef(null);
  const { studentsITeach, staff } = useContext(StaffContext);
  
  // The specific students the user wants to focus on
  const students = isManagerMode
    ? allStudentsContext.filter(
        (s) =>
          s.status === "Active" || s.status === "New" || s.status === "Pending",
      )
    : studentsITeach(user._id);
  
  // Format students for mentions
  const studentMentionData = useMemo(() => {
    if (!students) return [];
    return students.map(s => ({
      id: s._id,
      display: s.name,
      type: 'student',
      className: (s.classes && s.classes.length > 0) ? s.classes.map(c => c.name).join(", ") : ""
    }));
  }, [students]);

  // Format staff for mentions
  const staffMentionData = useMemo(() => {
    if (!staff) return [];
    return staff.map(s => ({
      id: s._id,
      display: s.name,
      type: 'staff'
    }));
  }, [staff]);

  // Unified list for the trigger
  const combinedMentionData = useMemo(() => {
    return [...studentMentionData, ...staffMentionData].sort((a, b) => a.display.localeCompare(b.display));
  }, [studentMentionData, staffMentionData]);
  

  // Fetch notes from the new API for the teacher
  const { get: serverNotes, loading, error, refresh: refreshNotes } = useDBO(`/get-student-notes/${user._id}`);

  // Intersection Observer for Infinite Scroll
  useEffect(() => {
    if (loading) return;
    
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        // If we reach the bottom, reveal more notes from the buffered set
        setVisibleCount(prev => prev + 10);
      }
    }, { threshold: 0.1 });

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
    };
  }, [loading, serverNotes]);

  const recentNotes = useMemo(() => {
    // 1. Start with the server-fetched notes
    const serverNotesArray = serverNotes || [];
    let combined = serverNotesArray.map(note => {
      // Lookup student name in context using studentId from API
      const student = allStudentsContext?.find(s => s._id === note.studentId);
      return {
        ...note,
        studentName: student?.name || "Student",
        date: note.date || note.createdAt // Standardize date field
      };
    });
    
    // 2. Merge with local notes from this session
    combined = [...localNotes, ...combined];

    // 3. Sort by date newest -> oldest
    const sorted = combined.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    // Check if we have more available than currently visible
    const hasMore = sorted.length > visibleCount;

    return {
      data: sorted.slice(0, visibleCount),
      hasMore
    };
  }, [serverNotes, allStudentsContext, user._id, localNotes, visibleCount]);

  const handleSubmit = async () => {
    if (!noteText.trim()) return;
    setIsSubmitting(true);
    
    // Combined regex for mentions as formatted by react-mentions: @[display](id)
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    let match;
    const studentIds = [];
    const staffIds = [];

    // Parse all mentions in the text
    while ((match = mentionRegex.exec(noteText)) !== null) {
      const id = match[2];
      // Check if this ID belongs to a student or staff in our defined data
      const isStudent = studentMentionData.some(s => s.id === id);
      const isStaff = staffMentionData.some(s => s.id === id);
      
      if (isStudent) studentIds.push(id);
      else if (isStaff) staffIds.push(id);
    }
    
    if (studentIds.length === 0) {
      alert(l["pleaseTagStudent"][language]);
      setIsSubmitting(false);
      return;
    }

    // 1. Remove mentions at the start (e.g. "@[John](123): was good" -> "was good")
    const leadingMentionRegex = /^(\s*@\[[^\]]+\]\([^)]+\)\s*[:.-]*\s*)+/;
    let plainTextNote = noteText.replace(leadingMentionRegex, "");

    // 2. Convert remaining mentions to plain text (removing @ markup)
    plainTextNote = plainTextNote.replace(mentionRegex, "$1");

    try {
      const requests = studentIds.map(async (studentId) => {
        const studentObj = studentMentionData.find(s => s.id === studentId);
        
        const notePayload = {
          note: plainTextNote.trim(),
          userId: user._id,
        };
        if(staffIds.length > 0) notePayload.assignedTo = staffIds[0];
        
        
        // Use the 'mut' hook for server side mutation
        const res = await mut("post", `/student/${studentId}/notes/add`, notePayload);
        
        return {
          ...res,
          _id: res?._id || Date.now().toString() + Math.random(),
          studentName: studentObj?.display || "Student",
          date: res?.date || new Date().toISOString(),
          recordedBy: user._id // Only store ID, let UI resolve Name/Avatar
        };
      });

      const results = await Promise.all(requests);
      
      setLocalNotes(prev => [...results.filter(r => r !== undefined), ...prev]);
      setNoteText("");
      
      // Refresh the server-side notes feed in the background
      if (refreshNotes) refreshNotes();
    } catch (err) {
      console.error("Failed to add note", err);
      alert(l["failedToSubmitNote"][language]);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!allStudentsContext) return <Loading />;

  return (
    <div className="home-widget student-notes-widget flex flex-col h-full bg-white border border-slate-200 rounded-[24px] shadow-sm overflow-hidden relative">
      <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 shrink-0 z-10">
        <h3 className="font-heading text-[1.25rem] font-semibold text-slate-900 flex items-center gap-2">
          <FileText size={22} className="text-teal-800" />
          <Lang w="quickNotes" />
        </h3>
        {hasElevatedRole && (
          <button
            onClick={() => setIsManagerMode(!isManagerMode)}
            className={`text-[0.65rem] font-[800] uppercase tracking-widest px-3 py-1.5 rounded-full transition-all cursor-pointer border hover:scale-105 active:scale-95 ${
              isManagerMode 
                ? "bg-teal-100 text-teal-700 border-teal-200 hover:bg-teal-200"
                : "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200"
            }`}
          >
            {isManagerMode ? (
              <span className="flex items-center gap-1.5">
                <Globe size={12} />
                {l["managerView"][language]}
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <GraduationCap size={12} />
                {l["myClasses"][language]}
              </span>
            )}
          </button>
        )}
      </div>
      
      {/* Notes Feed Container - Limited to ~3 notes with scroll */}
      <div className="flex-1 overflow-y-auto max-h-[420px] p-5 bg-white scrollbar-slim">
        {loading && !serverNotes ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Loading />
            <p className="text-xs font-bold text-slate-400 mt-2 animate-pulse uppercase tracking-widest">
              Fetching Notes...
            </p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-rose-500">
             <AlertTriangle size={36} className="mb-3" />
             <p className="text-sm font-bold">Failed to load notes</p>
             <button 
               onClick={() => refreshNotes && refreshNotes()}
               className="mt-4 text-xs bg-rose-50 text-rose-600 px-4 py-2 rounded-full border border-rose-100 hover:bg-rose-100 transition-colors font-bold uppercase tracking-wider"
             >
               Try Again
             </button>
          </div>
        ) : recentNotes.data.length === 0 ? (
<div className="flex flex-col items-center justify-center h-full text-center text-slate-400">
             <Inbox size={36} className="mb-3 opacity-30" />
             <p className="text-sm font-medium"><Lang w="noRecentNotes" /></p>
           </div>
        ) : (
          <div className="flex flex-col gap-3">
             {recentNotes.data.map((note) => {
               const noteDate = note.date ? new Date(note.date) : new Date();
               const student = allStudentsContext?.find(s => s._id === note.studentId);
               const staffMember = findStaff(note.recordedBy);
               
               return (
                 <div 
                   key={note._id} 
                   className="bg-white border border-slate-100 p-4 rounded-[16px] shadow-[0_2px_8px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_16px_-6px_rgba(0,0,0,0.05)] hover:border-teal-100 hover:bg-teal-50/30 transition-all duration-200"
                 >
                    {/* Note Card Header */}
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-3">
                        {/* Student Avatar */}
                        <Avatar 
                          src={student?.image ? `/images/${student.image}` : ""} 
                          sx={{ width: 36, height: 36, border: '2px solid #f8fafc', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} 
                        />
                        
                        <div className="flex flex-col">
                          <div className="font-heading font-semibold text-slate-800 text-[0.95rem] leading-tight">
                            <StudentLink student={{ _id: note.studentId, name: note.studentName }} tab="notes" />
                          </div>
                          {note.htClassId && (
<div className="text-[0.65rem] font-semibold text-teal-600 bg-teal-50 border border-teal-100 px-2 py-0.5 rounded-md mt-0.5 inline-flex items-center w-fit">
                               <School size={12} className="mr-1" />
                               <HtClass htClass={note.htClassId} link />
                             </div>
                          )}
                        </div>
                      </div>

                      {/* Info Chip: Date + Staff Avatar/Name */}
                      <div className="flex items-center gap-2 text-[0.65rem] font-semibold bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-full whitespace-nowrap shadow-sm min-w-[160px] justify-center">
                        <span className="text-slate-400">{formatDistanceToNow(noteDate, { addSuffix: true })}</span>
                        <span className="text-slate-200 text-lg leading-none select-none">•</span>
                        <div className="flex items-center gap-2 text-amber-600">
                          <Avatar 
                            src={staffMember?.image ? `/images/${staffMember.image}` : ""} 
                            sx={{ width: 18, height: 18, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }} 
                          />
                          <span className="max-w-[70px] overflow-hidden text-ellipsis">{staffMember?.name || findStaffName(note.recordedBy)}</span>
                        </div>
                      </div>
                    </div>

                    <p className="text-[0.875rem] text-slate-600 leading-[1.6] font-medium break-words px-1">
                      {note.note}
                    </p>
                  </div>
                );
              })}
              
              {/* Infinite Scroll Sentinel */}
              {recentNotes.hasMore && (
                <div ref={loadMoreRef} className="py-2 flex justify-center">
                  <div className="flex items-center gap-2 text-slate-300">
                    <div className="w-1 h-1 bg-teal-300 rounded-full animate-bounce"></div>
                    <div className="w-1 h-1 bg-teal-300 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div className="w-1 h-1 bg-teal-300 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  </div>
                </div>
              )}
           </div>
        )}
      </div>
      
      <div className="p-4 border-t border-slate-100 bg-slate-50 shrink-0">
        <div className="relative">
          <div className="relative text-slate-800">
            <MentionsInput
              value={noteText}
              onChange={(e, newValue) => setNoteText(newValue)}
              onKeyDown={(e) => {
                // Submit on Enter (but not Shift+Enter)
                if (e.key === 'Enter' && !e.shiftKey) {
                  // Only submit if we're NOT currently picking a suggestion
                  // react-mentions doesn't easily expose 'isSuggestionsOpened', but it prevents 
                  // default if it handles the enter key for a selection.
                  if (!e.defaultPrevented) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }
              }}
              style={defaultStyle}
              placeholder={l["typeToTag"][language]}
              a11ySuggestionsListLabel={l["suggestedStudents"][language]}
            >
              <Mention
                trigger="@"
                data={combinedMentionData}
                renderSuggestion={(suggestion, search, highlightedDisplay) => (
<div className="flex justify-between items-center w-full min-w-[240px] px-1 py-0.5">
                     <div className="flex items-center gap-2">
                        {suggestion.type === 'staff' ? (
                          <Briefcase size={16} className="text-slate-500" />
                        ) : (
                          <GraduationCap size={16} className="text-slate-500" />
                        )}
                        <span className="font-semibold text-slate-800">{highlightedDisplay}</span>
                     </div>
                    {suggestion.type === 'staff' ? (
                      <span className="text-[0.6rem] font-bold text-teal-500 bg-teal-50 border border-teal-100 px-2 py-0.5 rounded-full uppercase tracking-widest shrink-0 ml-4">
                        Staff
                      </span>
                    ) : (
                      <span className="text-[0.65rem] font-bold text-slate-400 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded ml-4 uppercase tracking-tighter shrink-0 max-w-[120px] truncate">
                        {suggestion.className}
                      </span>
                    )}
                  </div>
                )}
                displayTransform={(id, display) => `@${display}`}
                style={{ backgroundColor: '#f0fdfa', color: '#0f766e', borderRadius: '4px', fontWeight: '800' }}
              />
            </MentionsInput>
          </div>
          
          <div className="mt-3 flex justify-between items-center px-1">
            <span className="text-xs font-semibold text-slate-400">
              <span className="bg-slate-200 text-slate-500 rounded px-1 min-w-[20px] text-center inline-block">@</span> <Lang w="tagStudent" /> • <span className="bg-slate-200 text-slate-500 rounded px-1 inline-block">Enter</span> <Lang w="toSend" />
            </span>
            {isSubmitting && (
              <span className="text-xs font-bold text-teal-600 animate-pulse">
                <Lang w="sending" />
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
